import type { InspectionTemplate } from 'inspection-types';

import { applyChanges, type TemplateChange } from '../model/changes';
import type { PendingOp } from '../model/operations';
import type { CustomizationState, FailedOperation } from './customizationState';

export function isSectionExpanded(
  state: CustomizationState,
  sectionId: string
): boolean {
  return state.expandedBySectionId[sectionId] ?? false;
}

export function isSectionActive(
  state: CustomizationState,
  sectionId: string
): boolean {
  return state.activeSectionId === sectionId;
}

export function pendingOpFor(
  state: Pick<CustomizationState, 'pendingOps'>,
  targetId: string
): PendingOp | undefined {
  return Object.values(state.pendingOps).find(op => op.targetId === targetId);
}

// Object.values keeps the order the ops started in, which is the order the lane writes them
export function pendingReorderChanges(
  state: Pick<CustomizationState, 'pendingOps'>
): TemplateChange[] {
  return Object.values(state.pendingOps)
    .filter(op => op.kind === 'reorder')
    .map(op => op.change);
}

// Returns the template itself while no reorder is pending, so unchanged nodes keep their identity
export function applyPendingReorders(
  template: InspectionTemplate,
  state: Pick<CustomizationState, 'pendingOps'>
): InspectionTemplate {
  return applyChanges(template, pendingReorderChanges(state));
}

// What the user sees: the saved template with the pending reorders applied on top
export function renderedTemplate(
  state: Pick<CustomizationState, 'replica' | 'pendingOps'>
): InspectionTemplate | null {
  return state.replica ? applyPendingReorders(state.replica, state) : null;
}

export function failureFor(
  state: Pick<CustomizationState, 'failures'>,
  targetId: string
): FailedOperation | undefined {
  return Object.values(state.failures).find(
    ({ op }) => op.targetId === targetId
  );
}

export function isReplicaStale(state: CustomizationState): boolean {
  return state.staleReason !== null;
}
